import React, { useEffect, useState } from "react";
import { api } from "../api";
import Modal from "../components/Modal";

const CATEGORIES = [
  { value: "ski", label: "Ski" },
  { value: "boots", label: "Schuhe" },
  { value: "poles", label: "Stöcke" },
  { value: "binding", label: "Bindung" },
  { value: "lurk", label: "Lurk" },
];

const EMPTY_MODEL = { category: "ski", brand: "", model: "", size: "", notes: "" };
const EMPTY_ITEM = { equipment_id: "", quantity: 1, owner: "", home_location_id: "", serial_number: "" };

export default function Equipment() {
  const [equipment, setEquipment] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalModel, setModalModel] = useState(null);
  const [modalItem, setModalItem] = useState(null);
  const [error, setError] = useState(null);

  const reload = () => {
    setLoading(true);
    Promise.all([api.listEquipment(), api.listItems(), api.listLocations()])
      .then(([e, i, l]) => {
        setEquipment(e);
        setItems(i);
        setLocations(l);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const itemCounts = items.reduce((acc, i) => {
    acc[i.equipment_id] = (acc[i.equipment_id] || 0) + 1;
    return acc;
  }, {});

  const onSaveModel = async (m) => {
    setError(null);
    try {
      if (m.id) await api.updateEquipment(m.id, m);
      else await api.createEquipment(m);
      setModalModel(null);
      reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const onDeleteModel = async (id) => {
    if (!window.confirm("Modell wirklich löschen? Items müssen vorher entfernt werden.")) return;
    try {
      await api.deleteEquipment(id);
      reload();
    } catch (err) {
      alert(err.message);
    }
  };

  const onSaveItems = async (data) => {
    setError(null);
    try {
      await api.createItems(data);
      setModalItem(null);
      reload();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl">Equipment-Modelle</h1>
          <p className="text-fh-gray text-sm mt-1">{equipment.length} Modelle · {items.length} physische Items</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setModalItem(EMPTY_ITEM)} className="btn-secondary">+ Items hinzufügen</button>
          <button onClick={() => setModalModel(EMPTY_MODEL)} className="btn-primary">+ Neues Modell</button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-fh-gray">Lade…</div>
        ) : equipment.length === 0 ? (
          <div className="p-8 text-center text-fh-gray">Noch keine Modelle angelegt.</div>
        ) : (
          <>
            {/* Desktop: Tabelle */}
            <table className="w-full text-sm hidden md:table">
              <thead className="bg-fh-gray-light text-fh-gray text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">ID</th>
                  <th className="text-left px-4 py-2">Kategorie</th>
                  <th className="text-left px-4 py-2">Marke</th>
                  <th className="text-left px-4 py-2">Modell</th>
                  <th className="text-left px-4 py-2">Größe</th>
                  <th className="text-right px-4 py-2">Items</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {equipment.map((e) => (
                  <tr key={e.id} className="hover:bg-fh-gray-light">
                    <td className="px-4 py-2 font-mono text-xs">{e.id}</td>
                    <td className="px-4 py-2">{CATEGORIES.find((c) => c.value === e.category)?.label || e.category}</td>
                    <td className="px-4 py-2">{e.brand || "—"}</td>
                    <td className="px-4 py-2">{e.model}</td>
                    <td className="px-4 py-2">{e.size || "—"}</td>
                    <td className="px-4 py-2 text-right">{itemCounts[e.id] || 0}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setModalModel(e)} className="text-fh-gray hover:text-fh-black px-2 py-1">Bearbeiten</button>
                      <button onClick={() => onDeleteModel(e.id)} className="text-fh-gray hover:text-fh-red px-2 py-1">Löschen</button>
                      <button onClick={() => setModalItem({ ...EMPTY_ITEM, equipment_id: e.id })} className="text-fh-red hover:underline px-2 py-1">+ Item</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile: Karten */}
            <ul className="divide-y divide-gray-100 md:hidden">
              {equipment.map((e) => (
                <li key={e.id} className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium leading-tight">
                        {[e.brand, e.model, e.size].filter(Boolean).join(" ") || e.model}
                      </div>
                      <div className="text-xs text-fh-gray font-mono mt-0.5">{e.id}</div>
                    </div>
                    <span className="badge bg-fh-gray-light text-fh-black flex-shrink-0">
                      {itemCounts[e.id] || 0} Items
                    </span>
                  </div>
                  <div className="text-xs text-fh-gray mb-3">
                    {CATEGORIES.find((c) => c.value === e.category)?.label || e.category}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setModalItem({ ...EMPTY_ITEM, equipment_id: e.id })} className="btn-primary text-sm flex-1 min-w-[90px]">
                      + Item
                    </button>
                    <button onClick={() => setModalModel(e)} className="btn-secondary text-sm flex-1 min-w-[90px]">
                      Bearbeiten
                    </button>
                    <button onClick={() => onDeleteModel(e.id)} className="btn-ghost text-sm text-fh-red">
                      Löschen
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <ModelEditor
        model={modalModel}
        onClose={() => { setModalModel(null); setError(null); }}
        onSave={onSaveModel}
        error={error}
      />
      <ItemBatchEditor
        data={modalItem}
        equipment={equipment}
        locations={locations}
        onClose={() => { setModalItem(null); setError(null); }}
        onSave={onSaveItems}
        error={error}
      />
    </div>
  );
}

function ModelEditor({ model, onClose, onSave, error }) {
  const [form, setForm] = useState(EMPTY_MODEL);
  useEffect(() => { if (model) setForm({ ...EMPTY_MODEL, ...model }); }, [model]);
  if (!model) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isLurk = form.category === "lurk";

  return (
    <Modal
      open={!!model}
      title={form.id ? `Modell bearbeiten · ${form.id}` : "Neues Equipment-Modell"}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" onClick={() => onSave(form)} disabled={!form.model.trim()}>Speichern</button>
        </>
      }
    >
      {error && <div className="bg-fh-red/10 text-fh-red-dark text-sm px-3 py-2 rounded mb-3">{error}</div>}
      <div className="space-y-3">
        <div>
          <label className="label-field">Kategorie</label>
          <select className="input-field" value={form.category} onChange={(e) => set("category", e.target.value)}>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        {!isLurk && (
          <div>
            <label className="label-field">Marke</label>
            <input className="input-field" value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} placeholder="z.B. Fischer" />
          </div>
        )}
        <div>
          <label className="label-field">Modell {isLurk && "(Nummer 1, 2, 3 …)"}</label>
          <input className="input-field" value={form.model} onChange={(e) => set("model", e.target.value)} placeholder={isLurk ? "1" : "z.B. TM3"} />
        </div>
        {!isLurk && (
          <div>
            <label className="label-field">Größe</label>
            <input className="input-field" value={form.size || ""} onChange={(e) => set("size", e.target.value)} placeholder="z.B. 175" />
          </div>
        )}
        <div>
          <label className="label-field">Notiz</label>
          <textarea className="input-field" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

function ItemBatchEditor({ data, equipment, locations, onClose, onSave, error }) {
  const [form, setForm] = useState(EMPTY_ITEM);
  useEffect(() => { if (data) setForm({ ...EMPTY_ITEM, ...data }); }, [data]);
  if (!data) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Modal
      open={!!data}
      title="Items anlegen"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button
            className="btn-primary"
            onClick={() => onSave({
              equipment_id: form.equipment_id,
              quantity: Number(form.quantity) || 1,
              owner: form.owner || null,
              home_location_id: form.home_location_id || null,
              serial_number: form.serial_number || null,
            })}
            disabled={!form.equipment_id}
          >
            {form.quantity > 1 ? `${form.quantity} Items anlegen` : "Item anlegen"}
          </button>
        </>
      }
    >
      {error && <div className="bg-fh-red/10 text-fh-red-dark text-sm px-3 py-2 rounded mb-3">{error}</div>}
      <div className="space-y-3">
        <div>
          <label className="label-field">Modell *</label>
          <select className="input-field" value={form.equipment_id} onChange={(e) => set("equipment_id", e.target.value)}>
            <option value="">— wählen —</option>
            {equipment.map((e) => (
              <option key={e.id} value={e.id}>
                {[e.brand, e.model, e.size].filter(Boolean).join(" ") || e.id}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-field">Anzahl</label>
            <input type="number" min="1" max="100" className="input-field" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
          </div>
          <div>
            <label className="label-field">Eigentümer</label>
            <input className="input-field" value={form.owner || ""} onChange={(e) => set("owner", e.target.value)} placeholder="freeheeler / Partner XY" />
          </div>
        </div>
        <div>
          <label className="label-field">Heimat-Standort</label>
          <select className="input-field" value={form.home_location_id || ""} onChange={(e) => set("home_location_id", e.target.value)}>
            <option value="">—</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.is_partner ? " (Partner)" : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="label-field">Seriennummer (optional)</label>
          <input className="input-field" value={form.serial_number || ""} onChange={(e) => set("serial_number", e.target.value)} />
        </div>
        <p className="text-xs text-fh-gray">
          Bei Anzahl &gt; 1 werden Items automatisch durchnummeriert (A, B, C, …). Seriennummer wird bei allen identisch gesetzt.
        </p>
      </div>
    </Modal>
  );
}
